/**
 * Support Page
 *
 * Public support page for GRAutomates app - allows users to submit support requests.
 * Accessible at /support without authentication.
 * Fully responsive design for all devices and browsers.
 */

import { useState, useEffect } from 'react';
import axios from 'axios';
import Select from '../shared/Select';
import { useCurrentUser } from '../../contexts/UserContext';
import Button from '../shared/Button';
import { TEXT } from '../shared/Typography';
import Alert from '../shared/Alert';
import { FIELD_LABEL_CLASS, FIELD_INPUT_CLASS, FIELD_INPUT_ERROR_CLASS } from '../shared/FormInput';

interface SupportProps {
  isAuthenticated?: boolean;
}

interface FormData {
  name: string;
  email: string;
  subject: string;
  category: string;
  message: string;
}

interface FormErrors {
  name?: string;
  email?: string;
  subject?: string;
  message?: string;
}

type SubmitStatus = 'idle' | 'submitting' | 'success' | 'error';

export default function Support({ isAuthenticated = false }: SupportProps) {
  const _currentUser = useCurrentUser();
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    subject: '',
    category: 'general',
    message: '',
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitStatus, setSubmitStatus] = useState<SubmitStatus>('idle');
  const [submitMessage, setSubmitMessage] = useState('');

  // Check for stored user on mount to determine auth state
  const [isLoggedIn, setIsLoggedIn] = useState(isAuthenticated);

  useEffect(() => {
    try {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        const parsed = JSON.parse(storedUser);
        if (parsed?.token) {
          setIsLoggedIn(true);
        }
      }
    } catch {
      // Ignore parse errors
    }
  }, []);

  const categories = [
    { value: 'general', label: 'General Inquiry' },
    { value: 'technical', label: 'Technical Issue' },
    { value: 'account', label: 'Account Access' },
    { value: 'bug', label: 'Bug Report' },
    { value: 'feature', label: 'Feature Request' },
    { value: 'other', label: 'Other' },
  ];

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.subject.trim()) {
      newErrors.subject = 'Subject is required';
    }

    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    } else if (formData.message.trim().length < 10) {
      newErrors.message = 'Message must be at least 10 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setSubmitStatus('submitting');
    setSubmitMessage('');

    try {
      const response = await axios.post('/api/support/submit', formData);

      if (response.data.success) {
        setSubmitStatus('success');
        setSubmitMessage(
          response.data.message ||
            'Your support request has been submitted successfully. We will get back to you soon.'
        );
        setFormData({
          name: '',
          email: '',
          subject: '',
          category: 'general',
          message: '',
        });
      } else {
        setSubmitStatus('error');
        setSubmitMessage(response.data.error || 'Failed to submit support request.');
      }
    } catch (err: unknown) {
      setSubmitStatus('error');
      if (axios.isAxiosError(err) && err.response?.data?.error) {
        setSubmitMessage(err.response.data.error);
      } else {
        setSubmitMessage('An unexpected error occurred. Please try again later.');
      }
    }
  };

  const handleBack = () => {
    if (isLoggedIn) {
      window.location.href = '/';
    } else {
      window.location.href = 'https://grautomates.grps.com.au';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <button
                onClick={handleBack}
                className="p-2 -ml-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                aria-label="Go back"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  strokeWidth={2}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <div className="h-8 w-px bg-gray-200" />
              <img
                src="/grautomates-dark.png"
                alt="GRAutomates"
                className="h-8 w-auto object-contain"
              />
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-start justify-center px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <div className="w-full max-w-xl">
          {/* Card */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            {/* Header */}
            <div className="px-6 sm:px-8 py-6 sm:py-8 bg-white border-b border-gray-200">
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
                Contact Support
              </h1>
              <p className={`mt-2 ${TEXT.bodyMuted} sm:text-base`}>
                How can we help you today?
              </p>
            </div>

            {/* Form Content */}
            <div className="px-6 sm:px-8 py-6 sm:py-8">
              {submitStatus === 'success' ? (
                <div className="text-center py-6">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                    <svg
                      className="w-8 h-8 text-green-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      strokeWidth={2}
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h2 className={`mb-2 ${TEXT.titleLg}`}>
                    Request Submitted
                  </h2>
                  <p className={`mb-6 ${TEXT.bodyMuted} sm:text-base`}>
                    {submitMessage}
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <Button variant="primary" onClick={() => setSubmitStatus('idle')}>
                      Submit Another Request
                    </Button>
                    <Button variant="outline" onClick={handleBack}>
                      Return to App
                    </Button>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  {/* Error Alert */}
                  {submitStatus === 'error' && submitMessage && (
                    <Alert variant="error" message={submitMessage} />
                  )}

                  {/* Name and Email */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label
                        htmlFor="name"
                        className={FIELD_LABEL_CLASS}
                      >
                        Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        autoComplete="name"
                        className={errors.name ? FIELD_INPUT_ERROR_CLASS : FIELD_INPUT_CLASS}
                        placeholder="Your name"
                      />
                      {errors.name && (
                        <p className={`mt-1.5 ${TEXT.errorCaption}`}>{errors.name}</p>
                      )}
                    </div>

                    <div>
                      <label
                        htmlFor="email"
                        className={FIELD_LABEL_CLASS}
                      >
                        Email <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        autoComplete="email"
                        className={errors.email ? FIELD_INPUT_ERROR_CLASS : FIELD_INPUT_CLASS}
                        placeholder="you@example.com"
                      />
                      {errors.email && (
                        <p className={`mt-1.5 ${TEXT.errorCaption}`}>{errors.email}</p>
                      )}
                    </div>
                  </div>

                  {/* Category */}
                  <div>
                    <Select
                      label="Category"
                      value={formData.category}
                      onChange={(val) => {
                        setFormData((prev) => ({ ...prev, category: val }));
                      }}
                      options={categories.map((cat) => ({
                        value: cat.value,
                        label: cat.label,
                      }))}
                    />
                  </div>

                  {/* Subject */}
                  <div>
                    <label
                      htmlFor="subject"
                      className={FIELD_LABEL_CLASS}
                    >
                      Subject <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      className={errors.subject ? FIELD_INPUT_ERROR_CLASS : FIELD_INPUT_CLASS}
                      placeholder="Brief description of your issue"
                    />
                    {errors.subject && (
                      <p className={`mt-1.5 ${TEXT.errorCaption}`}>{errors.subject}</p>
                    )}
                  </div>

                  {/* Message */}
                  <div>
                    <label
                      htmlFor="message"
                      className={FIELD_LABEL_CLASS}
                    >
                      Message <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      rows={5}
                      value={formData.message}
                      onChange={handleChange}
                      className={`${errors.message ? FIELD_INPUT_ERROR_CLASS : FIELD_INPUT_CLASS} resize-none`}
                      placeholder="Please describe your issue or question in detail..."
                    />
                    {errors.message && (
                      <p className={`mt-1.5 ${TEXT.errorCaption}`}>{errors.message}</p>
                    )}
                  </div>

                  {/* Submit Button */}
                  <div className="pt-2">
                    <Button
                      variant="primary"
                      size="lg"
                      fullWidth
                      type="submit"
                      disabled={submitStatus === 'submitting'}
                    >
                      {submitStatus === 'submitting' ? (
                        <>
                          <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            />
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            />
                          </svg>
                          <span>Submitting...</span>
                        </>
                      ) : (
                        <>
                          <svg
                            className="w-5 h-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            strokeWidth={2}
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                            />
                          </svg>
                          <span>Submit Request</span>
                        </>
                      )}
                    </Button>
                  </div>
                </form>
              )}
            </div>
          </div>

          {/* Footer Links */}
          <div className="mt-6 text-center">
            <div className={`flex items-center justify-center gap-4 ${TEXT.helper}`}>
              <a
                href="/privacy-policy"
                className="hover:text-gray-700 transition-colors duration-200"
              >
                Privacy Policy
              </a>
              <span className="text-gray-300">|</span>
              <a
                href="https://grps.com.au"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-gray-700 transition-colors duration-200"
              >
                grps.com.au
              </a>
            </div>
            <p className={`mt-3 ${TEXT.caption}`}>
              &copy; {new Date().getFullYear()} GR Production Services Pty Ltd
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}
